import React, { useState, useEffect } from 'react';

const FooterLinksManagement = () => {
  const [footerLinks, setFooterLinks] = useState({
    company: [
      { label: 'About Us', url: '/about/company-values' },
      { label: 'Why River Prime', url: '/about/why-river-prime' },
      { label: 'Regulations', url: '/about/regulations' },
      { label: 'Contact Us', url: '#contact' }
    ],
    trading: [
      { label: 'Forex', url: '/markets/forex' },
      { label: 'Indices', url: '/markets/indices' },
      { label: 'Commodities', url: '/markets/commodities' },
      { label: 'Shares', url: '/markets/shares' }
    ],
    accounts: [
      { label: 'Pro Account', url: '/accounts/pro' },
      { label: 'Prime Account', url: '/accounts/prime' },
      { label: 'Premium Account', url: '/accounts/premium' },
      { label: 'Account Comparison', url: '/#accounts' }
    ],
    support: [
      { label: 'Help Center', url: '#' },
      { label: 'Trading Guides', url: '#' },
      { label: 'Platform Support', url: '#' },
      { label: 'Partnership', url: '/partnership' }
    ],
    legal: [
      { label: 'Terms of Service', url: '#' },
      { label: 'Privacy Policy', url: '#' },
      { label: 'Risk Disclosure', url: '#' },
      { label: 'Complaints', url: '#' }
    ]
  });

  const [footerSettings, setFooterSettings] = useState({
    companyName: 'RiverPrime',
    copyrightText: '© 2024 RiverPrime. All rights reserved.',
    disclaimer: 'Trading foreign exchange and CFDs involves significant risk and may not be suitable for all investors.',
    regulatoryText: 'RiverPrime is regulated by [Regulatory Body]'
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const savedLinks = localStorage.getItem('adminFooterLinks');
    const savedSettings = localStorage.getItem('adminFooterSettings');
    
    if (savedLinks) {
      setFooterLinks(JSON.parse(savedLinks));
    }
    if (savedSettings) {
      setFooterSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleLinkChange = (section, index, field, value) => {
    setFooterLinks(prev => ({
      ...prev,
      [section]: prev[section].map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
    setHasChanges(true);
  };

  const addLink = (section) => {
    setFooterLinks(prev => ({
      ...prev,
      [section]: [...prev[section], { label: 'New Link', url: '#' }]
    }));
    setHasChanges(true);
  };

  const removeLink = (section, index) => {
    setFooterLinks(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleSettingChange = (field, value) => {
    setFooterSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    localStorage.setItem('adminFooterLinks', JSON.stringify(footerLinks));
    localStorage.setItem('adminFooterSettings', JSON.stringify(footerSettings));
    setHasChanges(false);
    alert('Footer settings saved successfully!');
  };

  const footerSections = [
    { key: 'company', title: 'Company', icon: 'fas fa-building' },
    { key: 'trading', title: 'Trading', icon: 'fas fa-chart-line' },
    { key: 'accounts', title: 'Accounts', icon: 'fas fa-user-circle' },
    { key: 'support', title: 'Support', icon: 'fas fa-life-ring' },
    { key: 'legal', title: 'Legal', icon: 'fas fa-gavel' }
  ];

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Footer Links Management</h2>
        <p>Manage footer navigation links and company information</p>
      </div>

      {/* Footer Sections */}
      <div className="footer-sections">
        {footerSections.map((section) => (
          <div key={section.key} className="footer-section-card">
            <div className="section-title">
              <i className={section.icon}></i>
              <h3>{section.title}</h3>
              <button 
                className="add-link-btn"
                onClick={() => addLink(section.key)}
              >
                <i className="fas fa-plus"></i>
                Add Link
              </button>
            </div>
            
            <div className="links-list">
              {footerLinks[section.key].map((link, index) => (
                <div key={index} className="link-item">
                  <div className="link-inputs">
                    <input
                      type="text"
                      value={link.label}
                      onChange={(e) => handleLinkChange(section.key, index, 'label', e.target.value)}
                      placeholder="Link Label"
                      className="link-label-input"
                    />
                    <input
                      type="text"
                      value={link.url}
                      onChange={(e) => handleLinkChange(section.key, index, 'url', e.target.value)}
                      placeholder="Link URL"
                      className="link-url-input"
                    />
                  </div>
                  <button
                    className="remove-link-btn"
                    onClick={() => removeLink(section.key, index)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer Settings */}
      <div className="footer-settings">
        <h3>Footer Settings</h3>
        <div className="settings-grid">
          <div className="setting-item">
            <label>Company Name</label>
            <input
              type="text"
              value={footerSettings.companyName}
              onChange={(e) => handleSettingChange('companyName', e.target.value)}
              placeholder="RiverPrime"
            />
          </div>
          
          <div className="setting-item">
            <label>Copyright Text</label>
            <input
              type="text"
              value={footerSettings.copyrightText}
              onChange={(e) => handleSettingChange('copyrightText', e.target.value)}
              placeholder="© 2024 RiverPrime. All rights reserved."
            />
          </div>
          
          <div className="setting-item full-width">
            <label>Disclaimer Text</label>
            <textarea
              value={footerSettings.disclaimer}
              onChange={(e) => handleSettingChange('disclaimer', e.target.value)}
              placeholder="Trading foreign exchange and CFDs involves significant risk..."
              rows="3"
            />
          </div>
          
          <div className="setting-item full-width">
            <label>Regulatory Information</label>
            <textarea
              value={footerSettings.regulatoryText}
              onChange={(e) => handleSettingChange('regulatoryText', e.target.value)}
              placeholder="RiverPrime is regulated by..."
              rows="2"
            />
          </div>
        </div>
      </div>

      <div className="section-actions">
        <button 
          className="save-btn" 
          onClick={handleSave}
          disabled={!hasChanges}
        >
          <i className="fas fa-save"></i>
          Save Changes
        </button>
      </div>

      <div className="footer-preview">
        <h3>Footer Preview</h3>
        <div className="preview-footer">
          <div className="preview-sections">
            {footerSections.map((section) => (
              <div key={section.key} className="preview-section">
                <h4>{section.title}</h4>
                <ul>
                  {footerLinks[section.key].map((link, index) => (
                    <li key={index}>
                      <a href={link.url}>{link.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="preview-bottom">
            <div className="preview-copyright">{footerSettings.copyrightText}</div>
            <div className="preview-disclaimer">{footerSettings.disclaimer}</div>
            <div className="preview-regulatory">{footerSettings.regulatoryText}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterLinksManagement;